/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IStoryBlokAnnouncement,
  IStoryBlokAnnouncementBanner,
  IStoryBlokCookieBar,
  IStoryBlokNewsletterSignUpModal,
  IStoryBlokSeo,
} from "apps/website/components/page/api.types";
import {
  IStoryBlokAnnouncementBannerProps,
} from "apps/website/components/storyblok/page/StoryBlokAnnouncementBanner";
import { IHeaderLink } from "apps/website/components/base/Button/Button.map";
import {
  HEADER_LINKS_DEFAULT,
  HEADER_LINKS_BREEDER,
  IHeaderLinks,
  HEADER_LINKS_META,
  HEADER_LINKS_FRESH_FEAST_TRIAL,
  HEADER_LINKS_SIGNUP_FLOW_V2_AC,
  HEADER_LINKS_FUSSY_CAT_LANDING,
} from "apps/website/components/page/Header/Header.map";

import { makePageTags } from "../website/head";
import { renderStoryBlokRichText } from "../storyblok/text";
import { ImageMax, getStoryBlokImage } from "../storyblok/image";

export const getHeadData = (
  seo: IStoryBlokSeo | undefined,
  hideFromSearch: boolean | undefined,
  pageLastBuilt: string | undefined,
  fallbackTags: IStoryBlokSeo | undefined,
) => ({
  seo: seo ? makePageTags(seo) : undefined,
  hideFromSearch: hideFromSearch || false,
  lastBuilt: pageLastBuilt,
  fallbackTags: fallbackTags ? makePageTags(fallbackTags) : undefined,
});

export const getAnnouncementBannerData = (
  pageAnnouncements: IStoryBlokAnnouncement[],
  defaultAnnouncements: IStoryBlokAnnouncementBanner,
): IStoryBlokAnnouncementBannerProps => ({
  pageAnnouncements,
  defaultAnnouncements,
});

export const getCookieBannerData = (
  banner: IStoryBlokCookieBar,
) => ({
  body: renderStoryBlokRichText(banner.body),
  cta: { id: banner.cta[0]._uid, ...banner.cta[0] },
});

export const getNewsletterData = (
  newsletter: IStoryBlokNewsletterSignUpModal,
) => ({
  title: newsletter.title,
  completeTitle: newsletter.complete_title,
  body: newsletter.body,
  completeBody: newsletter.complete_body,
  buttonText: newsletter.button_text,
  theme: newsletter.theme,
  image: getStoryBlokImage(newsletter.image, true, ImageMax.CONTAINER_SM),
});

const mapLinksWithAssociatedDiscount = (links: IHeaderLink[], discountCode: string) => links.map((link) => ({ ...link, to: link.withAssociatedDiscount ? `${link.to}?discount_code=${discountCode}` : link.to }));

const getHeaderLinksWithAssociatedDiscount = (headerLinks: IHeaderLinks, discountCode: string | undefined) => {
  if (!discountCode) { return headerLinks; }
  return {
    primaryLinks: mapLinksWithAssociatedDiscount(headerLinks.primaryLinks, discountCode),
    secondaryLinks: mapLinksWithAssociatedDiscount(headerLinks.secondaryLinks, discountCode),
    authLinks: mapLinksWithAssociatedDiscount(headerLinks.authLinks, discountCode),
    guestLinks: mapLinksWithAssociatedDiscount(headerLinks.guestLinks, discountCode),
  };
};

const getHeaderLinksForStory = (story: any, associatedDiscount: string) => {
  if (story?.isFreshFeastTrialPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FRESH_FEAST_TRIAL, associatedDiscount);
  }
  if (story?.isSignupFlowV2AC) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_SIGNUP_FLOW_V2_AC, associatedDiscount);
  }
  if (story?.isFussyCatLandingPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_FUSSY_CAT_LANDING, associatedDiscount);
  }

  if (story?.isMetaSignUpFunnelPage) {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_META, associatedDiscount);
  }

  if (story?.main?.content?.header_navigation === "breeder") {
    return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_BREEDER, associatedDiscount);
  }

  return getHeaderLinksWithAssociatedDiscount(HEADER_LINKS_DEFAULT, associatedDiscount);
};

const getAnnouncementBannerIfEnabled = (story: any) => {
  if (!story?.main?.content?.announcements && !story?.announcementBanner) {
    return null;
  }
  return getAnnouncementBannerData(story.main?.content?.announcements, story.announcementBanner);
};

// @ts-ignore
export const getDefaultLayoutData = (story?: any, lastBuilt?: string, scripts?: HeaderScript[] = [ "trustpilot" ]): IPageLayout => { // eslint-disable-line
  const associatedDiscount = story?.main?.content?.associated_discount_code;

  return {
    headerLinks: getHeaderLinksForStory(story, associatedDiscount),
    head: story?.main && getHeadData(
      story.main.content.seo,
      story.main.content.hide_from_search_engines,
      lastBuilt,
      story.fallbackTags,
    ),
    scripts,
    announcementBanner: getAnnouncementBannerIfEnabled(story),
    cookieBanner: story?.cookieBar && getCookieBannerData(story.cookieBar),
    hideCookieBanner: story?.main?.content?.hide_cookie_bar,
    hideAnnouncementBanner: story?.main?.content?.hide_announcements || false,
    newsletterModal: story?.newsletterSignUpModal && getNewsletterData(story.newsletterSignUpModal),
    lastBuilt,
    jsonLd: story?.jsonLd,
    isMetaSignUpFunnelPage: story?.isMetaSignUpFunnelPage,
    isFreshFeastTrialPage: story?.isFreshFeastTrialPage,
    isSignupFlowV2AC: story?.isSignupFlowV2AC,
  };
};
